/**
 * User Settings Context
 * Global state management for user settings including MFA, Comply Admin, and auto-suspend
 * Provides centralized access to user preferences across the application
 */

import React, { createContext, useContext, useState, useEffect } from "react";
import { shouldUseAdminApi } from "../services/userApiService";

// Create context
const UserSettingsContext = createContext();

// Custom hook to use the user settings context
export const useUserSettings = () => {
  const context = useContext(UserSettingsContext);
  if (!context) {
    throw new Error("useUserSettings must be used within a UserSettingsProvider");
  }
  return context;
};

/**
 * User Settings Provider Component
 * Manages global user settings state and provides methods to access/update settings
 */
export const UserSettingsProvider = ({ children }) => {
  const [hasComplyAdmin, setHasComplyAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Load organization settings from API using centralized service
   * For super admins, always set hasComplyAdmin to true
   */
  useEffect(() => {
    const loadOrganizationSettings = async () => {
      try {
        setIsLoading(true);
        console.log("UserSettingsContext: Loading organization settings...");

        // Get user data from localStorage
        const userDataJSON = localStorage.getItem("token");
        if (!userDataJSON) {
          console.log("UserSettingsContext: No token found in localStorage");
          setHasComplyAdmin(false);
          setIsLoading(false);
          return;
        }

        const userData = JSON.parse(userDataJSON);

        // Use centralized user API service to check if admin API should be used
        // This handles both super admin check and organization settings
        const shouldUseApi = await shouldUseAdminApi();
        console.log("UserSettingsContext: Should use Admin API:", shouldUseApi);
        setHasComplyAdmin(shouldUseApi);
      } catch (error) {
        console.error("UserSettingsContext: Error loading organization settings:", error);
        setHasComplyAdmin(false);
      } finally {
        setIsLoading(false);
      }
    };

    loadOrganizationSettings();
  }, []);

  /**
   * Method to refresh settings (call after updating in AdminSettings or after login)
   * Uses centralized user API service
   * For super admins, always returns true
   */
  const refreshSettings = async () => {
    try {
      setIsLoading(true);
      const shouldUseApi = await shouldUseAdminApi();
      setHasComplyAdmin(shouldUseApi);
    } catch (error) {
      console.error("UserSettingsContext: Error refreshing settings:", error);
      setHasComplyAdmin(false);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Legacy refresh method that directly fetches settings
   * Kept for backward compatibility
   */
  const refreshSettingsDirect = async () => {
    try {
      console.log("UserSettingsContext: Refreshing settings...");

      // Get user data from localStorage
      const userDataJSON = localStorage.getItem("token");
      if (!userDataJSON) {
        setHasComplyAdmin(false);
        return;
      }

      const userData = JSON.parse(userDataJSON);

      // Use centralized user API service to check if admin API should be used
      const shouldUseApi = await shouldUseAdminApi();
      console.log("UserSettingsContext: Refresh - Should use Admin API:", shouldUseApi);
      setHasComplyAdmin(shouldUseApi);
    } catch (error) {
      console.error("UserSettingsContext: Error refreshing settings:", error);
    }
  };

  const value = {
    hasComplyAdmin,
    isLoading,
    refreshSettings, // Triggers re-fetch by incrementing triggerRefresh
    refreshSettingsDirect, // Direct fetch without triggering useEffect
    setHasComplyAdmin, // Expose setter for manual updates
  };

  return <UserSettingsContext.Provider value={value}>{children}</UserSettingsContext.Provider>;
};

export default UserSettingsContext;
